import { Router, type IRouter } from "express";
import Stripe from "stripe";
import { db, paymentsTable, usersTable } from "@workspace/db";
import { eq } from "drizzle-orm";
import { CreateCheckoutSessionBody } from "@workspace/api-zod";
import { requireAuth } from "../middlewares/auth";
import { logger } from "../lib/logger";

const router: IRouter = Router();

const STRIPE_SECRET_KEY = process.env.STRIPE_SECRET_KEY;
const stripe = STRIPE_SECRET_KEY ? new Stripe(STRIPE_SECRET_KEY) : null;

const PLAN_PRICES: Record<string, { amount: number; name: string; mode: "subscription" | "payment" }> = {
  vip: { amount: 99900, name: "VIP Investor — AED 999/month", mode: "subscription" },
  elite: { amount: 299900, name: "Elite Investor — AED 2999/month", mode: "subscription" },
  verified_broker: { amount: 99900, name: "Verified Broker — AED 999/month", mode: "subscription" },
  urgent_seller: { amount: 500000, name: "Urgent Seller Campaign — AED 5,000", mode: "payment" },
  concierge_sale: { amount: 1500000, name: "Private Concierge Sale — AED 15,000", mode: "payment" },
};

router.post("/payments/create-checkout", requireAuth, async (req, res): Promise<void> => {
  const parsed = CreateCheckoutSessionBody.safeParse(req.body);
  if (!parsed.success) {
    res.status(400).json({ error: parsed.error.message });
    return;
  }

  const { plan, successUrl, cancelUrl } = parsed.data;
  const planInfo = PLAN_PRICES[plan];
  if (!planInfo) {
    res.status(400).json({ error: "Invalid plan" });
    return;
  }

  if (!stripe) {
    logger.warn("Stripe not configured — returning mock session");
    const sessionId = `mock_session_${plan}_${Date.now()}`;
    await db.insert(paymentsTable).values({
      userId: req.auth!.userId,
      stripeSessionId: sessionId,
      plan: plan as "vip" | "elite" | "verified_broker" | "urgent_seller" | "concierge_sale",
      amount: String(planInfo.amount / 100),
      status: "pending",
    });
    // For mock: auto-upgrade membership for investor plans
    if (plan === "vip" || plan === "elite") {
      await db.update(usersTable).set({ membership: plan, paymentStatus: "paid" }).where(eq(usersTable.id, req.auth!.userId));
    } else if (plan === "verified_broker") {
      await db.update(usersTable).set({ membership: "vip", paymentStatus: "paid" }).where(eq(usersTable.id, req.auth!.userId));
    }
    res.json({ sessionId, url: null });
    return;
  }

  const [user] = await db.select().from(usersTable).where(eq(usersTable.id, req.auth!.userId));

  const lineItem = {
    price_data: {
      currency: "aed",
      product_data: { name: planInfo.name },
      unit_amount: planInfo.amount,
      ...(planInfo.mode === "subscription" ? { recurring: { interval: "month" as const } } : {}),
    },
    quantity: 1,
  };

  const session = await stripe.checkout.sessions.create({
    payment_method_types: ["card"],
    mode: planInfo.mode,
    customer_email: user?.email,
    line_items: [lineItem],
    success_url: successUrl ?? `${req.headers.origin}/pricing?success=1`,
    cancel_url: cancelUrl ?? `${req.headers.origin}/pricing?cancel=1`,
    metadata: {
      userId: String(req.auth!.userId),
      plan,
    },
  });

  await db.insert(paymentsTable).values({
    userId: req.auth!.userId,
    stripeSessionId: session.id,
    plan: plan as "vip" | "elite" | "verified_broker" | "urgent_seller" | "concierge_sale",
    amount: String(planInfo.amount / 100),
    status: "pending",
  });

  res.json({ sessionId: session.id, url: session.url });
});

router.post("/payments/webhook", async (req, res): Promise<void> => {
  const sig = req.headers["stripe-signature"];
  const webhookSecret = process.env.STRIPE_WEBHOOK_SECRET;

  if (!stripe || !sig || !webhookSecret) {
    res.json({ received: true });
    return;
  }

  let event: Stripe.Event;
  try {
    event = stripe.webhooks.constructEvent(req.body as Buffer, sig, webhookSecret);
  } catch (err) {
    logger.error({ err }, "Webhook signature verification failed");
    res.status(400).json({ error: "Webhook error" });
    return;
  }

  if (event.type === "checkout.session.completed") {
    const session = event.data.object as Stripe.Checkout.Session;
    const userId = session.metadata?.userId;
    const plan = session.metadata?.plan;

    if (userId && plan) {
      const uid = parseInt(userId, 10);
      if (plan === "vip" || plan === "elite") {
        await db.update(usersTable).set({ membership: plan, paymentStatus: "paid" }).where(eq(usersTable.id, uid));
      } else if (plan === "verified_broker") {
        await db.update(usersTable).set({ paymentStatus: "paid" }).where(eq(usersTable.id, uid));
      }
      await db
        .update(paymentsTable)
        .set({ status: "completed" })
        .where(eq(paymentsTable.stripeSessionId, session.id));
    }
  }

  res.json({ received: true });
});

export default router;
